import { propertySectionLabel } from './field-labels';
import { isExplicitlyClosed } from './issues/status';
import type { EnforcementUpdate, NormalizedPage, NormalizedTable, ParcelRecord } from './types';

/** Whether a complaint's case history ends with the case abated or closed. */
export function complaintAbated(pages: NormalizedPage[]) {
  let abated = false;
  for (const page of pages) {
    for (const table of page.tables) {
      const column = table.headers.findIndex((header) => /^status$/i.test(header.trim()));
      if (column < 0) continue;
      for (const row of table.rows) {
        const status = row[column]?.trim() ?? '';
        if (/^case\s+(?:abated|closed)$/i.test(status)) abated = true;
        else if (/reopen/i.test(status)) abated = false;
      }
    }
  }
  return abated;
}

/**
 * DBI never updates a notice's own status; the notice is resolved once every
 * complaint it was issued under is closed or abated.
 */
export function novResolved(record: ParcelRecord, records: ParcelRecord[]) {
  if (record.kind !== 'nov') return false;
  if (isExplicitlyClosed(record.status, null, 'dbi', 'nov')) return true;
  const complaints = relatedEnforcementRecords(record, records).filter(
    (related) => related.kind === 'complaint',
  );
  return (
    complaints.length > 0 &&
    complaints.every(
      (complaint) =>
        isExplicitlyClosed(complaint.status, null, 'dbi', 'complaint') ||
        complaintAbated(complaint.pages),
    )
  );
}

export type EnforcementEntry = {
  key: string;
  identity: string;
  label: string;
  fields: Record<string, string>;
  summary: string;
};

function normalized(value: string) {
  return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function headerIndex(table: NormalizedTable, names: string[]) {
  const headers = table.headers.map(normalized);
  return (
    names
      .map(normalized)
      .map((name) => headers.indexOf(name))
      .find((index) => index >= 0) ?? -1
  );
}

function rowValue(table: NormalizedTable, row: string[], names: string[]) {
  const index = headerIndex(table, names);
  return index >= 0 ? (row[index] ?? '') : '';
}

function fieldValue(record: ParcelRecord, names: string[]) {
  const wanted = new Set(names.map(normalized));
  for (const page of record.pages) {
    for (const [field, value] of Object.entries(page.fields)) {
      if (wanted.has(normalized(field)) && value) return value;
    }
  }
  return '';
}

function enforcementLabel(table: NormalizedTable) {
  if (/dgstatus|complaint status/i.test(table.title)) return 'Complaint status and comments';
  if (/dgitems/i.test(table.title)) return 'NOV items';
  return table.title || 'Enforcement history';
}

export function isEnforcementTable(table: NormalizedTable) {
  const headers = new Set(table.headers.map(normalized));
  const statusComments = headers.has('date') && headers.has('status') && headers.has('comment');
  const novItems = headers.has('item') && headers.has('description');
  const namedHistory =
    /complaint|nov|violation|case history|abatement|action|status/i.test(table.title) &&
    (headers.has('comment') || headers.has('description') || headers.has('action'));
  return statusComments || novItems || namedHistory;
}

function entrySummary(label: string, table: NormalizedTable, row: string[]) {
  const date = rowValue(table, row, ['Date', 'Action Date']);
  const type = rowValue(table, row, ['Type', 'Action', 'Item']);
  const status = rowValue(table, row, ['Status']);
  const comment = rowValue(table, row, ['Comment', 'Description', 'Remarks']);
  const inspector = rowValue(table, row, ['Inspector']);
  const updatedBy = rowValue(table, row, ['Updated By']);
  const division = rowValue(table, row, ['Div', 'Division']);
  return (
    [
      propertySectionLabel(label),
      date,
      type,
      status,
      comment,
      inspector && `Inspector: ${inspector}`,
      updatedBy && `Updated by: ${updatedBy}`,
      division && `Division: ${division}`,
    ]
      .filter(Boolean)
      .join(' · ') || row.filter(Boolean).join(' · ')
  );
}

export function enforcementEntries(record: ParcelRecord): EnforcementEntry[] {
  if (record.kind !== 'complaint' && record.kind !== 'nov') return [];
  const occurrences = new Map<string, number>();
  const entries: EnforcementEntry[] = [];

  for (const page of record.pages) {
    for (const table of page.tables) {
      if (!isEnforcementTable(table)) continue;
      const label = enforcementLabel(table);
      for (const row of table.rows.filter((candidate) => candidate.some(Boolean))) {
        const fields = Object.fromEntries(
          table.headers.map((header, index) => [header || `Column ${index + 1}`, row[index] ?? '']),
        );
        const identity =
          [
            rowValue(table, row, ['Date', 'Action Date']),
            rowValue(table, row, ['Type', 'Action']),
            rowValue(table, row, ['Item']),
            rowValue(table, row, ['Div', 'Division']),
          ]
            .filter(Boolean)
            .join(' · ') ||
          row[0] ||
          'Entry';
        const occurrenceKey = `${label} · ${identity}`;
        const occurrence = (occurrences.get(occurrenceKey) ?? 0) + 1;
        occurrences.set(occurrenceKey, occurrence);
        entries.push({
          key: `${occurrenceKey} · ${occurrence}`,
          identity: occurrenceKey,
          label,
          fields,
          summary: entrySummary(label, table, row),
        });
      }
    }
  }
  return entries;
}

function dateValue(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function enforcementDetails(record: ParcelRecord): EnforcementUpdate[] {
  if (record.kind !== 'complaint' && record.kind !== 'nov') return [];

  const tableUpdates = record.pages
    .flatMap((page) => page.tables.filter(isEnforcementTable))
    .flatMap((table) => {
      const label = enforcementLabel(table);
      return table.rows
        .filter((row) => row.some(Boolean))
        .map((row) => ({
          label,
          date: rowValue(table, row, ['Date', 'Action Date']) || record.date,
          type: rowValue(table, row, ['Type', 'Action', 'Item']),
          status: rowValue(table, row, ['Status', 'Type']),
          comment: rowValue(table, row, ['Comment', 'Description', 'Remarks']),
          actor: [rowValue(table, row, ['Inspector']), rowValue(table, row, ['Updated By'])]
            .filter(Boolean)
            .join(' · '),
          inspector: rowValue(table, row, ['Inspector']),
          updatedBy: rowValue(table, row, ['Updated By']),
          division: rowValue(table, row, ['Div', 'Division']),
          sourceUrl: record.sourceUrl,
        }));
    });

  if (record.kind === 'nov') {
    const failureComment = fieldValue(record, ['Failure Comment', 'Failurecommentdesc']);
    const violationDescription = fieldValue(record, ['Violation Description', 'Codeviolationdesc']);
    const correctionPeriod = fieldValue(record, [
      'Correction Period',
      'Correctvioldays',
      'Compliance Period',
    ]);
    const reinspectionDate = fieldValue(record, ['Reinspection Date', 'Reinsp Date']);
    const inspector =
      fieldValue(record, ['Reinspection Name', 'Reinsp Name']) ||
      [fieldValue(record, ['Inspector Firstname']), fieldValue(record, ['Inspector Lastname'])]
        .filter(Boolean)
        .join(' ');
    const division = fieldValue(record, ['Division Id']);
    const comment = [
      failureComment || violationDescription,
      correctionPeriod
        ? `Correction period: ${correctionPeriod}${/^\d+$/.test(correctionPeriod) ? ' days' : ''}`
        : '',
    ]
      .filter(Boolean)
      .join(' · ');
    if (comment || reinspectionDate || inspector || division) {
      tableUpdates.unshift({
        label: 'NOV details',
        date: reinspectionDate || record.date,
        type: '',
        status: record.status,
        comment,
        actor: inspector,
        inspector,
        updatedBy: '',
        division,
        sourceUrl: record.sourceUrl,
      });
    }
  }

  return tableUpdates.sort((a, b) => dateValue(b.date) - dateValue(a.date));
}

function enforcementSourceIdentity(value: string) {
  try {
    const url = new URL(value);
    if (
      !['https:', 'http:'].includes(url.protocol) ||
      url.hostname !== 'dbiweb02.sfgov.org' ||
      !/^\/dbipts\/default2?\.aspx$/i.test(url.pathname) ||
      url.username ||
      url.password
    )
      return undefined;
    const params = new Map([...url.searchParams].map(([key, value]) => [key.toLowerCase(), value]));
    const page = params.get('page')?.toLowerCase();
    if (page === 'addresscomplaint' && /^\d+$/.test(params.get('complaintno') ?? ''))
      return `complaint:${params.get('complaintno')}`;
    if (
      (page === 'webnovdetails' || page === 'webhisnovdetails') &&
      /^\d+-\d+$/.test(params.get('comp_id') ?? '')
    )
      return `nov:${params.get('comp_id')}`;
  } catch {
    return undefined;
  }
  return undefined;
}

/** Join only city-published case references; a NOV ID is not a complaint number. */
export function relatedEnforcementRecords(record: ParcelRecord, records: ParcelRecord[]) {
  if (record.kind !== 'complaint' && record.kind !== 'nov') return [];
  const identity = enforcementSourceIdentity(record.sourceUrl);
  if (!identity) return [];
  const linksTo = (source: ParcelRecord, target: string) =>
    source.pages.some((page) =>
      page.links.some((link) => enforcementSourceIdentity(link.url) === target),
    );
  return records.filter((other) => {
    if (other.kind === record.kind || (other.kind !== 'complaint' && other.kind !== 'nov'))
      return false;
    const otherIdentity = enforcementSourceIdentity(other.sourceUrl);
    if (!otherIdentity) return false;
    if (linksTo(record, otherIdentity) || linksTo(other, identity)) return true;
    const nov = record.kind === 'nov' ? record : other;
    const complaint = record.kind === 'complaint' ? record : other;
    const complaintNumber = fieldValue(nov, [
      'Complaint number',
      'Complaint no',
      'Complaint num',
    ]).trim();
    return /^\d+$/.test(complaintNumber) && complaintNumber === complaint.id;
  });
}

export function relatedEnforcementSources(record: ParcelRecord, records: ParcelRecord[]) {
  return relatedEnforcementRecords(record, records).map((related) => ({
    label: `${related.kind === 'complaint' ? 'Complaint case history' : 'Notice of violation'} · ${related.id}`,
    url: related.sourceUrl,
  }));
}
